// components/ProfessionalsList.tsx
import React, { useState, useEffect } from "react";
import api from "../api.js";
import "../style/css/ProfessionalsList.css";

// Interfaces TypeScript
interface Professional {
  id: number;
  name: string;
  email: string;
  status: "active" | "inactive" | "pending";
  registration_date: string;
  appointments_completed: number;
  specialty: string;
  avatar_url?: string;
}

interface ProfessionalsResponse {
  professionals: Professional[];
  total_count: number;
  active_count: number;
  inactive_count: number;
  pending_count: number;
}

// SVG Icons
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.55 8H17.5c-.76 0-1.45.43-1.79 1.11L15 12.5v-1.28c0-.83-.67-1.5-1.5-1.5h-5c-.83 0-1.5.67-1.5 1.5V19h8v2h4zm-7.5-10.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM6 6.5c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM7.5 19v-6H5v6h2.5zm4 0v-6H9v6h2.5z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
  </svg>
);

const StethoscopeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 8c0 1.26-.38 2.43-1.03 3.4-.03.04-.07.08-.1.13-.6.87-1.03 1.87-1.03 2.97 0 2.21 1.79 4 4 4s4-1.79 4-4c0-1.1-.43-2.1-1.03-2.97-.03-.05-.07-.09-.1-.13-.65-.97-1.03-2.14-1.03-3.4V6h-2v2c0 1.1-.9 2-2 2s-2-.9-2-2V6h-2v2zm-6 4.5c0 .55.45 1 1 1s1-.45 1-1v-1h1c.55 0 1-.45 1-1s-.45-1-1-1h-1v-1c0-.55-.45-1-1-1s-1 .45-1 1v1h-1c-.55 0-1 .45-1 1s.45 1 1 1h1v1zM4 9c0 2.21 1.79 4 4 4s4-1.79 4-4c0-.71-.19-1.38-.51-1.96l.51-.51c.39-.39.39-1.02 0-1.41-.39-.39-1.02-.39-1.41 0l-.63.63C9.47 5.33 8.78 5 8 5c-.78 0-1.47.33-1.96.86l-.63-.63c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41l.51.51C4.19 7.62 4 8.29 4 9z" />
  </svg>
);

const ProfessionalsList: React.FC = () => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total_count: 0,
    active_count: 0,
    inactive_count: 0,
    pending_count: 0,
  });

  useEffect(() => {
    fetchProfessionals();
  }, []);

  const fetchProfessionals = async () => {
    try {
      setLoading(true);
      const response = await api.get("/profiles/admin/");

      if (!response) {
        throw new Error("Error al cargar los profesionales");
      }

      const data: ProfessionalsResponse = response.data;
      setProfessionals(data.professionals);
      setStats({
        total_count: data.total_count,
        active_count: data.active_count,
        inactive_count: data.inactive_count,
        pending_count: data.pending_count,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      setLoading(false);
    }
  };

  const getTimeSinceRegistration = (registrationDate: string): string => {
    const now = new Date();
    const registration = new Date(registrationDate);
    const diffTime = Math.abs(now.getTime() - registration.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 día";
    if (diffDays < 30) return `${diffDays} días`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} mes${months > 1 ? "es" : ""}`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} año${years > 1 ? "s" : ""}`;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: "Activo", class: "status-badge--active" },
      inactive: { label: "Inactivo", class: "status-badge--inactive" },
      pending: { label: "Pendiente", class: "status-badge--pending" },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <span className={`status-badge ${config.class}`}>{config.label}</span>
    );
  };

  if (loading) {
    return (
      <div className="professionals-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando profesionales...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="professionals-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button className="btn btn--primary" onClick={fetchProfessionals}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="professionals-container">
      <div className="professionals-header">
        <h1>Gestión de Profesionales</h1>
        <p>Administra y visualiza el equipo médico de tu clínica</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card__icon">
            <UsersIcon />
          </div>
          <div className="stat-card__content">
            <h3>{stats.total_count}</h3>
            <p>Total Profesionales</p>
          </div>
        </div>
        <div className="stat-card stat-card--active">
          <div className="stat-card__icon">
            <CheckIcon />
          </div>
          <div className="stat-card__content">
            <h3>{stats.active_count}</h3>
            <p>Activos</p>
          </div>
        </div>
        <div className="stat-card stat-card--inactive">
          <div className="stat-card__icon">
            <PauseIcon />
          </div>
          <div className="stat-card__content">
            <h3>{stats.inactive_count}</h3>
            <p>Inactivos</p>
          </div>
        </div>
        <div className="stat-card stat-card--pending">
          <div className="stat-card__icon">
            <ClockIcon />
          </div>
          <div className="stat-card__content">
            <h3>{stats.pending_count}</h3>
            <p>Pendientes</p>
          </div>
        </div>
      </div>

      {/* Professionals Grid */}
      <div className="professionals-grid">
        {professionals.map((professional) => (
          <div key={professional.id} className="professional-card">
            <div className="professional-card__header">
              <div className="professional-avatar">
                {professional.avatar_url ? (
                  <img
                    src={professional.avatar_url || "/user_placeholder.svg"}
                    alt={professional.name}
                    className="avatar-img"
                  />
                ) : (
                  <div className="avatar-placeholder">
                    {professional.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                )}
              </div>
              <div className="professional-info">
                <h3 className="professional-name">{professional.name}</h3>
                <p className="professional-specialty">
                  {professional.specialty}
                </p>
                {getStatusBadge(professional.status)}
              </div>
            </div>

            <div className="professional-card__details">
              <div className="detail-item">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{professional.email}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Registrado hace:</span>
                <span className="detail-value">
                  {getTimeSinceRegistration(professional.registration_date)}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Citas completadas:</span>
                <span className="detail-value highlight">
                  {professional.appointments_completed}
                </span>
              </div>
            </div>

            <div className="professional-card__actions">
              <button className="btn btn--secondary btn--small">
                Ver Detalles
              </button>
              <button className="btn btn--primary btn--small">Editar</button>
            </div>
          </div>
        ))}
      </div>

      {professionals.length === 0 && (
        <div className="empty-state">
          <div className="empty-state__icon">
            <StethoscopeIcon />
          </div>
          <h3>No hay profesionales registrados</h3>
          <p>Comienza agregando el primer profesional a tu clínica</p>
          <button className="btn btn--primary btn--large">
            Agregar Profesional
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfessionalsList;
